import React, { useState, useEffect } from 'react';
import { collection, doc, onSnapshot, query, orderBy, limit, updateDoc, addDoc, serverTimestamp, increment, getDocs } from 'firebase/firestore';
import { db } from '../../services/firebase';
import { useAuth } from '../../context/AuthContext';

export default function EekoPayDashboard({ onClose }) {
  const { currentUser } = useAuth();
  const [activeTab, setActiveTab] = useState('liquidity'); 

  const [vendorBalances, setVendorBalances] = useState({});
  const [integrations, setIntegrations] = useState({});
  
  const [fundingVendor, setFundingVendor] = useState(null);
  const [fundingAmount, setFundingAmount] = useState('');

  const [clients, setClients] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedClient, setSelectedClient] = useState(null);
  const [ledgerEntries, setLedgerEntries] = useState([]);
  const [ledgerLoading, setLedgerLoading] = useState(false);

  const [autoReplenish, setAutoReplenish] = useState(false);
  const [threshold, setThreshold] = useState(50);
  const [replenishAmount, setReplenishAmount] = useState(200);
  const [isSavingConfig, setIsSavingConfig] = useState(false);

  const [adjustType, setAdjustType] = useState('CREDIT');
  const [adjustAmount, setAdjustAmount] = useState('');
  const [adjustMemo, setAdjustMemo] = useState('');
  const [isSubmittingAdjust, setIsSubmittingAdjust] = useState(false);

  // NEW: Enterprise Cryptographic Audit State
  const [securityFlags, setSecurityFlags] = useState([]);

  useEffect(() => {
    const unsubBalances = onSnapshot(doc(db, 'system_config', 'vendor_wallets'), (snap) => {
      if (snap.exists()) setVendorBalances(snap.data());
    });
    
    const unsubConfig = onSnapshot(doc(db, 'system_config', 'global_settings'), (snap) => {
      if (snap.exists() && snap.data().active_integrations) {
        setIntegrations(snap.data().active_integrations);
      }
    });

    const unsubClients = onSnapshot(collection(db, 'clients'), (snap) => {
      const clientList = [];
      snap.forEach((docSnap) => clientList.push({ id: docSnap.id, ...docSnap.data() }));
      setClients(clientList);
    });
    
    return () => { unsubBalances(); unsubConfig(); unsubClients(); };
  }, []);

  // NEW: Real-Time Cryptographic Ledger Reconciliation Audit
  useEffect(() => {
    const auditLedgers = async () => {
      const flags = [];
      for (const client of clients) {
        const ledgerRef = collection(db, 'clients', client.id, 'eeko_ledger');
        const snap = await getDocs(ledgerRef);
        
        let verifiedSum = 0;
        snap.forEach(docSnap => {
          const tx = docSnap.data();
          const amt = Number(tx.amount || 0);
          // Reconcile mathematical additions vs subtractions
          verifiedSum += tx.type === 'CREDIT' ? amt : -amt;
        });
        
        const claimedBalance = Number(client.billing?.walletBalance || 0);
        const unverifiedFunds = claimedBalance - verifiedSum;
        
        // Trap any discrepancy greater than a floating point margin
        if (Math.abs(unverifiedFunds) > 0.01) {
          flags.push({
            entity: client.companyName || client.id,
            claimedBalance: claimedBalance,
            verifiedLedger: verifiedSum,
            desyncAmount: unverifiedFunds
          });
        }
      }
      setSecurityFlags(flags);
    };
    
    if (clients.length > 0) {
      auditLedgers();
    }
  }, [clients]);

  useEffect(() => {
    if (!selectedClient) { setLedgerEntries([]); return; }
    setLedgerLoading(true);
    setAutoReplenish(selectedClient.billing?.autoReplenish ?? false);
    setThreshold(selectedClient.billing?.autoReplenishThreshold ?? 50);
    setReplenishAmount(selectedClient.billing?.autoReplenishAmount ?? 200);

    const ledgerRef = collection(db, 'clients', selectedClient.id, 'eeko_ledger');
    const ledgerQuery = query(ledgerRef, orderBy('timestamp', 'desc'), limit(50));
    const unsubLedger = onSnapshot(ledgerQuery, (snap) => {
      const entries = [];
      snap.forEach((docSnap) => entries.push({ id: docSnap.id, ...docSnap.data() }));
      setLedgerEntries(entries);
      setLedgerLoading(false);
    });
    return () => unsubLedger();
  }, [selectedClient?.id]);

  const handleFundVendorWallet = async (e) => {
    e.preventDefault();
    if (!fundingVendor || !fundingAmount || Number(fundingAmount) <= 0) return;
    try {
      const vendorRef = doc(db, 'system_config', 'vendor_wallets');
      await updateDoc(vendorRef, {
        [fundingVendor]: increment(Number(fundingAmount)),
        updatedAt: serverTimestamp(),
        lastFundedBy: currentUser?.email || 'SYS_FOUNDER'
      });
      setFundingVendor(null); setFundingAmount('');
    } catch (err) { alert(`Failed to update vendor float: ${err.message}`); }
  };

  const handleSaveAutoPayRules = async () => {
    if (!selectedClient) return;
    setIsSavingConfig(true);
    try {
      const clientRef = doc(db, 'clients', selectedClient.id);
      await updateDoc(clientRef, {
        'billing.autoReplenish': autoReplenish,
        'billing.autoReplenishThreshold': Number(threshold),
        'billing.autoReplenishAmount': Number(replenishAmount),
        updatedAt: serverTimestamp()
      });
      alert('Automated treasury rules saved.');
    } catch (err) { alert(`Configuration update failed: ${err.message}`); } 
    finally { setIsSavingConfig(false); }
  };

  const handleManualAdjustment = async (e) => {
    e.preventDefault();
    if (!selectedClient || !adjustAmount || Number(adjustAmount) <= 0 || !adjustMemo.trim()) return;
    setIsSubmittingAdjust(true);
    const numericAmount = Number(adjustAmount);
    const balanceDelta = adjustType === 'CREDIT' ? numericAmount : -numericAmount;
    try {
      const clientRef = doc(db, 'clients', selectedClient.id);
      const ledgerCollection = collection(db, 'clients', selectedClient.id, 'eeko_ledger');
      await addDoc(ledgerCollection, {
        amount: numericAmount,
        type: adjustType,
        serviceCategory: 'MANUAL_ADJUSTMENT',
        memo: adjustMemo.trim(),
        actorEmail: currentUser?.email || 'SYS_FOUNDER',
        timestamp: serverTimestamp()
      });
      await updateDoc(clientRef, {
        'billing.walletBalance': increment(balanceDelta),
        updatedAt: serverTimestamp()
      });
      setAdjustAmount(''); setAdjustMemo('');
    } catch (err) { alert(`Adjustment failed: ${err.message}`); } 
    finally { setIsSubmittingAdjust(false); }
  };

  const filteredClients = clients.filter(c => 
    c.companyName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    c.id.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const preFundedVendors = Object.entries(integrations)
    .map(([key, data]) => ({ id: key, ...data }))
    .filter(v => v.billingType === 'PRE_FUNDED' || ['zendit', 'checkhq', 'easypost', 'sendgrid', 'openai', 'imei_info'].includes(v.id));

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 backdrop-blur-sm p-4 lg:p-8 animate-in fade-in duration-200 font-sans">
      <div className="bg-white border border-slate-200 w-full max-w-7xl h-[92vh] rounded shadow-2xl flex flex-col overflow-hidden text-slate-900">
        
        <div className="bg-slate-50 px-6 py-4 border-b border-slate-200 flex justify-between items-center shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-emerald-100 border border-emerald-200 flex items-center justify-center text-emerald-600 rounded shadow-sm">
              <i className="fa-solid fa-building-columns text-sm"></i>
            </div>
            <div>
              <h2 className="text-base font-black uppercase tracking-wider text-slate-900">Treasury & Ledger Command</h2>
              <p className="text-[10px] text-slate-500 uppercase tracking-widest font-bold">Reinhardt SaaS Technologies LLC</p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <div className="bg-slate-200/50 p-1 rounded flex gap-1">
              <button 
                onClick={() => setActiveTab('liquidity')}
                className={`px-4 py-1.5 text-[10px] font-black uppercase tracking-widest rounded transition-colors ${activeTab === 'liquidity' ? 'bg-white shadow-sm text-slate-900' : 'text-slate-500 hover:text-slate-700'}`}
              >
                Network Liquidity
              </button>
              <button 
                onClick={() => setActiveTab('yield')}
                className={`px-4 py-1.5 text-[10px] font-black uppercase tracking-widest rounded transition-colors ${activeTab === 'yield' ? 'bg-white shadow-sm text-slate-900' : 'text-slate-500 hover:text-slate-700'}`}
              >
                Corporate Yield
              </button>
            </div>
            <button 
              onClick={onClose} 
              className="text-slate-400 hover:text-slate-800 px-3 py-1.5 border border-slate-200 hover:bg-slate-100 rounded text-xs uppercase tracking-widest font-black transition-colors ml-4"
            >
              Close <i className="fa-solid fa-xmark ml-1"></i>
            </button>
          </div>
        </div>

        {/* SECURITY AUDIT TRAP - Renders immediately if ghost funds exist */}
        {securityFlags.length > 0 && (
          <div className="bg-red-50 border-b border-red-200 p-4 shrink-0 overflow-y-auto max-h-48">
            <div className="flex items-center gap-2 mb-3 text-red-700 font-black uppercase tracking-widest text-sm">
              <i className="fa-solid fa-triangle-exclamation"></i> CRITICAL: LEDGER INTEGRITY DESYNC DETECTED
            </div>
            <div className="space-y-2">
              {securityFlags.map((flag, idx) => (
                <div key={idx} className="flex flex-wrap lg:flex-nowrap justify-between items-center bg-white p-3 rounded border border-red-200 shadow-sm gap-4">
                  <div>
                    <span className="text-[10px] font-black uppercase tracking-widest text-slate-800 block">{flag.entity}</span>
                    <span className="text-[9px] font-bold uppercase tracking-widest text-red-500">Unverified Fund Injection Detected</span>
                  </div>
                  <div className="flex flex-wrap gap-4 text-right">
                    <div className="px-3">
                      <span className="text-[8px] font-bold uppercase tracking-widest text-slate-400 block">Claimed Balance</span>
                      <span className="text-[10px] font-mono font-black text-slate-900">${flag.claimedBalance.toFixed(2)}</span>
                    </div>
                    <div className="px-3">
                      <span className="text-[8px] font-bold uppercase tracking-widest text-slate-400 block">Verified Ledger</span>
                      <span className="text-[10px] font-mono font-black text-slate-900">${flag.verifiedLedger.toFixed(2)}</span>
                    </div>
                    <div className="bg-red-100 px-3 py-1 rounded border border-red-200">
                      <span className="text-[8px] font-black uppercase tracking-widest text-red-700 block">Ghost Funds</span>
                      <span className="text-xs font-mono font-black text-red-700">${flag.desyncAmount.toFixed(2)}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {activeTab === 'liquidity' ? (
          <>
            <div className="bg-white border-b border-slate-200 p-6 shrink-0">
              <div className="flex justify-between items-center mb-4">
                <span className="text-[10px] font-black uppercase tracking-widest text-slate-700 flex items-center gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-500"></span> Tier 1 Pre-Funded API Escrow Wallets
                </span>
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
                {preFundedVendors.length === 0 ? (
                  <div className="col-span-full p-4 border border-dashed border-slate-300 rounded text-center text-[10px] font-black uppercase tracking-widest text-slate-400">
                    No pre-funded partners registered in API Registry.
                  </div>
                ) : (
                  preFundedVendors.map((vendor) => {
                    const balance = Number(vendorBalances[vendor.id] || 0);
                    const isLow = balance < 50; 
                    return (
                      <div key={vendor.id} className={`p-4 rounded border shadow-sm flex flex-col justify-between ${isLow ? 'bg-red-50 border-red-200' : 'bg-white border-slate-200'}`}>
                        <div className="flex justify-between items-center mb-2">
                          <span className="text-[10px] font-black uppercase tracking-wider text-slate-500 truncate pr-2">{vendor.name || vendor.id}</span>
                          <button 
                            onClick={() => { setFundingVendor(vendor.id); setFundingAmount(''); }}
                            className="text-[9px] text-emerald-600 hover:text-emerald-800 font-black uppercase tracking-widest bg-emerald-50 px-2 py-0.5 rounded border border-emerald-100 shrink-0"
                          >
                            Fund
                          </button>
                        </div>
                        <p className={`text-lg font-black ${isLow ? 'text-red-600' : 'text-slate-900'}`}>
                          ${balance.toFixed(2)}
                        </p>
                      </div>
                    );
                  })
                )}
              </div>
            </div>

            <div className="flex-1 flex overflow-hidden bg-slate-50">
              <div className="flex-1 flex flex-col border-r border-slate-200 bg-white m-4 rounded shadow-sm overflow-hidden">
                <div className="p-4 border-b border-slate-200 flex justify-between items-center gap-4 bg-slate-50">
                  <div className="relative flex-1">
                    <i className="fa-solid fa-magnifying-glass absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-xs"></i>
                    <input 
                      type="text"
                      placeholder="Search accounts..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="w-full bg-white border border-slate-300 text-slate-900 pl-9 pr-4 py-2 text-[11px] font-bold uppercase tracking-wider focus:outline-none focus:border-emerald-500 rounded placeholder:text-slate-400 shadow-inner"
                    />
                  </div>
                  <span className="text-[10px] font-bold text-slate-500 uppercase tracking-widest whitespace-nowrap bg-white px-3 py-1.5 border border-slate-200 rounded">
                    {filteredClients.length} Ledgers
                  </span>
                </div>

                <div className="flex-1 overflow-y-auto">
                  <table className="w-full text-left text-[11px]">
                    <thead className="bg-slate-100 text-slate-500 uppercase tracking-widest text-[9px] sticky top-0 border-b border-slate-200 shadow-sm">
                      <tr>
                        <th className="py-3 px-4 font-black">Entity</th>
                        <th className="py-3 px-4 font-black">Tier</th>
                        <th className="py-3 px-4 font-black text-right">Float Balance</th>
                        <th className="py-3 px-4 font-black text-center">Auto-Pay</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {filteredClients.map((client) => {
                        const balance = Number(client.billing?.walletBalance || 0);
                        const isSelected = selectedClient?.id === client.id;
                        const isCritical = balance < 20;

                        return (
                          <tr key={client.id} onClick={() => setSelectedClient(client)} className={`cursor-pointer transition-colors ${isSelected ? 'bg-emerald-50' : 'hover:bg-slate-50'}`}>
                            <td className="py-3 px-4">
                              <div className={`font-black uppercase ${isSelected ? 'text-emerald-900' : 'text-slate-900'} flex items-center`}>
                                {client.companyName || 'Unknown Entity'}
                                <span className={`ml-2 px-1.5 py-0.5 rounded text-[8px] font-mono tracking-widest ${isSelected ? 'bg-emerald-200/50 text-emerald-800' : 'bg-slate-100 text-slate-400 border border-slate-200'}`}>
                                  #{client.id.slice(-6).toUpperCase()}
                                </span>
                              </div>
                              <div className="text-[9px] text-slate-400 font-mono tracking-wider mt-1">{client.id}</div>
                            </td>
                            <td className="py-3 px-4">
                              <span className={`text-[8px] font-black uppercase px-2 py-0.5 rounded border ${client.tenantType === 'MASTER_AGENT' ? 'bg-purple-50 text-purple-700 border-purple-200' : 'bg-blue-50 text-blue-700 border-blue-200'}`}>
                                {client.tenantType || 'DEALER'}
                              </span>
                            </td>
                            <td className="py-3 px-4 text-right font-mono font-black">
                              <span className={isCritical ? 'text-red-600 bg-red-50 px-2 py-1 rounded' : 'text-emerald-600'}>
                                ${balance.toFixed(2)}
                              </span>
                            </td>
                            <td className="py-3 px-4 text-center">
                              {client.billing?.autoReplenish ? (
                                <span className="text-[9px] font-black text-emerald-700 uppercase tracking-widest bg-emerald-100 px-2 py-0.5 rounded">ON</span>
                              ) : (
                                <span className="text-[9px] font-black text-slate-400 uppercase tracking-widest">OFF</span>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="w-full lg:w-[420px] bg-white flex flex-col shrink-0 border border-slate-200 m-4 ml-0 rounded shadow-sm">
                {selectedClient ? (
                  <div className="flex-1 flex flex-col overflow-hidden">
                    <div className="p-5 border-b border-slate-200 bg-slate-50">
                      <div className="flex justify-between items-start mb-2">
                        <h3 className="text-sm font-black text-slate-900 uppercase tracking-tight">{selectedClient.companyName}</h3>
                        <span className="text-[9px] font-black bg-white border border-slate-200 text-slate-500 px-2 py-0.5 rounded uppercase tracking-widest">
                          {selectedClient.tenantType}
                        </span>
                      </div>
                      <p className="text-[10px] font-mono font-bold text-slate-500">Wallet Balance:</p>
                      <p className="text-2xl font-black text-emerald-600 tracking-tight">${Number(selectedClient.billing?.walletBalance || 0).toFixed(2)}</p>
                    </div>

                    <div className="p-5 border-b border-slate-200">
                      <div className="flex justify-between items-start mb-4">
                        <div>
                          <span className="text-[10px] font-black text-slate-700 uppercase tracking-widest block">ACH Auto-Replenish Matrix</span>
                          <span className="text-[8px] font-bold text-slate-400 uppercase tracking-widest mt-0.5 block">Restricted to verified bank transfers (ACH).</span>
                        </div>
                        <button onClick={() => setAutoReplenish(!autoReplenish)} className={`text-[9px] font-black uppercase px-3 py-1 rounded transition-colors ${autoReplenish ? 'bg-emerald-600 text-white shadow-sm' : 'bg-slate-200 text-slate-500'}`}>
                          {autoReplenish ? 'Enabled' : 'Disabled'}
                        </button>
                      </div>

                      <div className="grid grid-cols-2 gap-4 mb-4">
                        <div>
                          <label className="block text-[9px] font-bold uppercase tracking-widest text-slate-500 mb-1.5">If drops below</label>
                          <div className="relative">
                            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 font-bold text-xs">$</span>
                            <input type="number" value={threshold} onChange={(e) => setThreshold(Number(e.target.value))} className="w-full bg-white border border-slate-300 text-slate-900 pl-7 pr-2 py-1.5 text-xs font-mono font-bold focus:outline-none focus:border-emerald-500 rounded shadow-inner" />
                          </div>
                        </div>
                        <div>
                          <label className="block text-[9px] font-bold uppercase tracking-widest text-slate-500 mb-1.5">Draft ACH Amount</label>
                          <div className="relative">
                            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 font-bold text-xs">$</span>
                            <input type="number" value={replenishAmount} onChange={(e) => setReplenishAmount(Number(e.target.value))} className="w-full bg-white border border-slate-300 text-slate-900 pl-7 pr-2 py-1.5 text-xs font-mono font-bold focus:outline-none focus:border-emerald-500 rounded shadow-inner" />
                          </div>
                        </div>
                      </div>

                      <button onClick={handleSaveAutoPayRules} disabled={isSavingConfig} className="w-full bg-white hover:bg-slate-50 text-slate-700 text-[10px] font-black uppercase tracking-widest py-2 rounded border border-slate-300 transition-colors shadow-sm">
                        {isSavingConfig ? 'Saving...' : 'Save Configuration'}
                      </button>
                    </div>

                    <div className="flex-1 flex flex-col overflow-hidden">
                      <div className="px-5 py-3 bg-slate-50 border-b border-slate-200 flex justify-between items-center text-[9px] font-black uppercase tracking-widest text-slate-500 shadow-sm z-10">
                        <span>Ledger Stream</span><span>Last 50</span>
                      </div>
                      <div className="flex-1 overflow-y-auto divide-y divide-slate-100 p-2 bg-white">
                        {ledgerLoading ? (
                          <div className="p-8 text-center text-xs text-slate-400 font-bold uppercase tracking-widest">Loading...</div>
                        ) : ledgerEntries.length === 0 ? (
                          <div className="p-8 text-center text-xs text-slate-400 font-bold uppercase tracking-widest">No History</div>
                        ) : (
                          ledgerEntries.map((tx) => (
                            <div key={tx.id} className="p-3 hover:bg-slate-50 rounded transition-colors">
                              <div className="flex justify-between items-center mb-1.5">
                                <span className="text-[10px] font-black text-slate-700 uppercase tracking-wider">{tx.memo || tx.serviceCategory}</span>
                                <span className={`font-mono font-black text-xs ${tx.type === 'CREDIT' ? 'text-emerald-600' : 'text-slate-700'}`}>
                                  {tx.type === 'CREDIT' ? '+' : '-'}${Number(tx.amount).toFixed(2)}
                                </span>
                              </div>
                              <div className="flex justify-between items-center text-[8px] text-slate-400 uppercase tracking-widest">
                                <span>{tx.actorEmail || 'System'}</span><span>{tx.timestamp?.toDate ? tx.timestamp.toDate().toLocaleTimeString() : 'Pending'}</span>
                              </div>
                            </div>
                          ))
                        )}
                      </div>
                    </div>

                    <form onSubmit={handleManualAdjustment} className="p-4 border-t border-slate-200 bg-slate-50 shrink-0">
                      <div className="flex items-center justify-between mb-3">
                        <span className="text-[9px] font-black uppercase tracking-widest text-slate-600">Manual Adjustment</span>
                        <div className="flex gap-1 bg-white p-1 rounded border border-slate-200">
                          <button type="button" onClick={() => setAdjustType('CREDIT')} className={`text-[8px] font-black uppercase px-3 py-1 rounded ${adjustType === 'CREDIT' ? 'bg-emerald-600 text-white' : 'text-slate-500 hover:bg-slate-100'}`}>Credit (+)</button>
                          <button type="button" onClick={() => setAdjustType('DEBIT')} className={`text-[8px] font-black uppercase px-3 py-1 rounded ${adjustType === 'DEBIT' ? 'bg-slate-800 text-white' : 'text-slate-500 hover:bg-slate-100'}`}>Debit (-)</button>
                        </div>
                      </div>

                      <div className="flex gap-2 mb-3">
                        <div className="w-1/3 relative">
                          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 font-bold text-xs">$</span>
                          <input type="number" step="0.01" placeholder="0.00" value={adjustAmount} onChange={(e) => setAdjustAmount(e.target.value)} className="w-full bg-white border border-slate-300 text-slate-900 pl-7 pr-2 py-2 text-xs font-mono font-bold focus:outline-none focus:border-emerald-500 rounded shadow-inner" />
                        </div>
                        <div className="w-2/3">
                          <input type="text" placeholder="Memo required" value={adjustMemo} onChange={(e) => setAdjustMemo(e.target.value)} className="w-full bg-white border border-slate-300 text-slate-900 px-3 py-2 text-[10px] font-bold uppercase tracking-wider focus:outline-none focus:border-emerald-500 rounded shadow-inner placeholder:text-slate-400" />
                        </div>
                      </div>
                      <button type="submit" disabled={isSubmittingAdjust || !adjustAmount || !adjustMemo.trim()} className="w-full bg-slate-900 hover:bg-slate-800 disabled:bg-slate-200 disabled:text-slate-400 text-white text-[10px] font-black uppercase tracking-widest py-2.5 rounded shadow-sm transition-colors">
                        {isSubmittingAdjust ? 'Processing...' : 'Execute Entry'}
                      </button>
                    </form>

                  </div>
                ) : (
                  <div className="flex-1 flex flex-col items-center justify-center p-8 text-center text-slate-400">
                    <div className="w-12 h-12 rounded-full bg-slate-100 flex items-center justify-center mb-3 border border-slate-200">
                      <i className="fa-solid fa-arrow-left text-lg text-slate-300"></i>
                    </div>
                    <p className="text-[10px] font-black uppercase tracking-widest text-slate-500">Select an Account</p>
                    <p className="text-[9px] mt-1">Audit ledgers and configure auto-pay.</p>
                  </div>
                )}
              </div>
            </div>
          </>
        ) : (
          <div className="flex-1 bg-slate-50 overflow-y-auto p-8">
            <div className="max-w-4xl mx-auto">
              <div className="mb-8 text-center">
                <h2 className="text-2xl font-black uppercase tracking-tight text-slate-900">Corporate Yield & Revenue</h2>
                <p className="text-xs text-slate-500 font-bold uppercase tracking-widest mt-1">Reinhardt SaaS Technologies LLC • Real-Time Margins</p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                <div className="bg-white p-6 rounded border border-slate-200 shadow-sm text-center">
                  <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2">MTD Margin Spread</p>
                  <p className="text-3xl font-black text-emerald-600">$0.00</p>
                  <p className="text-[8px] font-bold text-slate-400 uppercase tracking-widest mt-2 border-t border-slate-100 pt-2">API Ping & Service Yield</p>
                </div>
                <div className="bg-white p-6 rounded border border-slate-200 shadow-sm text-center">
                  <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2">Stripe Connect Kickbacks</p>
                  <p className="text-3xl font-black text-emerald-600">$0.00</p>
                  <p className="text-[8px] font-bold text-slate-400 uppercase tracking-widest mt-2 border-t border-slate-100 pt-2">Master Agent Revenue Share</p>
                </div>
                <div className="bg-white p-6 rounded border border-slate-200 shadow-sm text-center">
                  <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2">Total MTD Revenue</p>
                  <p className="text-3xl font-black text-slate-900">$0.00</p>
                  <p className="text-[8px] font-bold text-slate-400 uppercase tracking-widest mt-2 border-t border-slate-100 pt-2">Gross Platform Yield</p>
                </div>
              </div>

              <div className="bg-white rounded border border-slate-200 shadow-sm overflow-hidden">
                <div className="bg-slate-900 p-4 flex justify-between items-center">
                  <h3 className="text-[10px] font-black text-white uppercase tracking-widest">Global Micro-Transaction Yield Stream</h3>
                  <span className="bg-slate-800 text-slate-300 text-[8px] px-2 py-0.5 rounded border border-slate-700 uppercase tracking-widest">Awaiting Backend Aggregation Sync</span>
                </div>
                <div className="p-16 text-center text-slate-400">
                  <i className="fa-solid fa-chart-network text-3xl mb-3 text-slate-300"></i>
                  <p className="text-xs font-black uppercase tracking-widest text-slate-500">Cron Jobs Pending Deployment</p>
                  <p className="text-[10px] max-w-sm mx-auto mt-2 leading-relaxed">
                    The nightly Cloud Scheduler map-reduce functions have not been deployed to aggregate the $0.05 ledger deductions into this master view. Once live, this area will stream a consolidated feed of every micro-margin captured across the ecosystem.
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {fundingVendor && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-4">
          <div className="bg-white border border-slate-200 max-w-sm w-full p-6 rounded shadow-2xl">
            <h3 className="text-sm font-black text-slate-900 uppercase tracking-tight mb-1">
              Top-Up Escrow: <span className="text-emerald-600">{fundingVendor}</span>
            </h3>
            <p className="text-[10px] text-slate-500 uppercase tracking-widest mb-5 font-bold">Manual wire reconciliation.</p>

            <form onSubmit={handleFundVendorWallet} className="space-y-5">
              <div>
                <label className="block text-[9px] font-black uppercase tracking-widest text-slate-600 mb-1.5">Amount Funded (USD)</label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 font-bold text-sm">$</span>
                  <input type="number" step="1" required placeholder="500.00" value={fundingAmount} onChange={(e) => setFundingAmount(e.target.value)} className="w-full bg-slate-50 border border-slate-300 text-slate-900 pl-8 pr-3 py-2.5 text-sm font-mono font-black focus:outline-none focus:border-emerald-500 rounded shadow-inner" />
                </div>
              </div>

              <div className="flex gap-2">
                <button type="button" onClick={() => setFundingVendor(null)} className="flex-1 bg-white hover:bg-slate-50 border border-slate-200 text-slate-600 text-[10px] font-black uppercase tracking-widest py-2.5 rounded transition-colors shadow-sm">Cancel</button>
                <button type="submit" className="flex-1 bg-slate-900 hover:bg-slate-800 text-white text-[10px] font-black uppercase tracking-widest py-2.5 rounded shadow-sm transition-colors">Record Wire</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}